import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { go } from "./Nav";
import { useLang } from "./i18n";

const EASE = [0.76, 0, 0.24, 1];

export const Line = ({ children, delay, ready, className = "" }) => (
  <span className="block overflow-hidden pt-[0.14em] -mt-[0.14em] pb-[0.06em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={ready ? { y: "0%" } : { y: "110%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Ring = () => (
  <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full og-spin" aria-hidden>
    <defs>
      <path id="ring" d="M200,200 m-180,0 a180,180 0 1,1 360,0 a180,180 0 1,1 -360,0" />
    </defs>
    <text className="f-mono" fill="#F5F5F7" fontSize="15" letterSpacing="6">
      <textPath href="#ring">WELCOME TO OFF GRID ✦ WELCOME TO OFF GRID ✦ WELCOME TO OFF GRID ✦ </textPath>
    </text>
  </svg>
);

export const Hero = ({ ready }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [18, -18]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-22, 22]), { stiffness: 120, damping: 18 });
  const cx = useSpring(useMotionValue(-100), { stiffness: 500, damping: 40 });
  const cy = useSpring(useMotionValue(-100), { stiffness: 500, damping: 40 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bigX = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const emblemY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    cx.set(e.clientX - r.left);
    cy.set(e.clientY - r.top);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[#050505] pt-[120px] md:cursor-none"
      style={{ perspective: 1200 }}
    >
      <motion.div style={{ x: bigX }} className="absolute bottom-[4vh] left-0 whitespace-nowrap f-goth text-[38vw] md:text-[26vw] leading-none og-outline select-none pointer-events-none">
        Off Grid Off Grid
      </motion.div>
      <div className="absolute inset-0 og-spot pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 grid md:grid-cols-12 gap-8 px-5 md:px-10 min-h-[calc(100svh-120px)] items-center">
        <div className="md:col-span-7 order-2 md:order-1 pb-16 md:pb-0">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: ready ? 1 : 0 }} transition={{ delay: 0.2 }} className="f-mono text-xs tracking-[0.3em] text-[#FF2A2A] mb-6">
            {t("hero.kicker")}
          </motion.div>
          <h1 data-testid="hero-title" className="f-disp uppercase text-[17vw] sm:text-[13vw] md:text-[7.4vw] leading-[0.85] tracking-tight text-[#F5F5F7]">
            <Line ready={ready} delay={0.1}>{t("hero.l1")}</Line>
            <Line ready={ready} delay={0.22}>{t("hero.l2")}<span className="text-[#FF2A2A]">.</span></Line>
            <Line ready={ready} delay={0.34} className="f-goth normal-case text-[#FF2A2A] tracking-normal text-[0.62em] leading-[1.05]">Welcome to Off Grid</Line>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.8, duration: 0.8 }} className="mt-8 max-w-md text-zinc-300 text-sm md:text-base leading-relaxed">
            {t("hero.text")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.95, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-shop-button" onClick={() => go("#nouveautes")} className="og-btn og-btn--red">
              <span>{t("hero.cta")}</span><ArrowDownRight className="w-4 h-4" />
            </button>
            <button data-testid="hero-lookbook-button" onClick={() => go("#lookbook")} className="og-btn og-btn--ghost">
              <span>Lookbook</span>
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: emblemY }} className="md:col-span-5 order-1 md:order-2 flex justify-center">
          <motion.div
            data-testid="hero-emblem"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={ready ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative w-[78vw] h-[78vw] max-w-[300px] max-h-[300px] md:max-w-[460px] md:max-h-[460px] md:w-[34vw] md:h-[34vw]"
          >
            <Ring />
            <div className="absolute inset-[14%] rounded-full bg-[radial-gradient(circle,rgba(255,42,42,0.35),transparent_65%)]" style={{ transform: "translateZ(-60px)" }} />
            <img src="/img/logo.gif" alt="Logo Off Grid" className="absolute inset-[12%] w-[76%] h-[76%] og-invert" style={{ transform: "translateZ(80px)" }} />
          </motion.div>
        </motion.div>
      </motion.div>

      <div className="hidden md:block absolute bottom-5 left-1/2 -translate-x-1/2 z-10 f-mono text-[11px] tracking-[0.3em] text-zinc-400">SCROLL ↓</div>
      <div className="absolute bottom-5 right-5 md:right-10 z-10 f-mono text-[11px] tracking-[0.3em] text-zinc-400">N 45°30' — OFFLINE</div>

      <motion.div style={{ x: cx, y: cy }} className="hidden md:block pointer-events-none absolute top-0 left-0 z-20 -ml-3 -mt-3 w-6 h-6 rounded-full bg-[#FF2A2A] mix-blend-difference" />
    </section>
  );
};
